/**
 * Reaction processor — evaluates FieldReaction[] and returns computed overrides
 * for a single field. Used inside FormFieldAdapter to dynamically adjust field
 * properties (visible, required, disabled, label, options, etc.).
 */

import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { FieldReaction } from '../types/fieldMapping';
import type { Condition } from '../types/conditions';
import type { Compute } from '../types/compute';
import { CONDITION_KEYS } from '../types/conditions';
import { evaluateCondition } from '../evaluators/conditionEvaluator';
import type { ConditionContext } from '../evaluators/conditionEvaluator';
import { evaluateCompute } from '../evaluators/computeEvaluator';
import { prefixCondition, prefixCompute } from './pathRewriter';

export interface ReactionOverrides {
  visible?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  options?: Array<{ label: string; value: unknown }>;
  label?: string;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
}

interface UseReactionsParams {
  reactions: FieldReaction[] | undefined;
  form: UseFormReturn<Record<string, unknown>>;
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  namePrefix?: string;
}

export interface ComputeReactionOverridesParams {
  reactions: FieldReaction[] | undefined;
  formValues: Record<string, unknown>;
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  namePrefix?: string;
}

/** Pure reaction evaluation — shared by useReactions and useMatrixVisibility. */
export function computeReactionOverrides({
  reactions,
  formValues,
  meta,
  getOptionProp,
  namePrefix,
}: ComputeReactionOverridesParams): ReactionOverrides {
  if (!reactions || reactions.length === 0) return {};

  const overrides: ReactionOverrides = {};

  for (const reaction of reactions) {
    const watches = Array.isArray(reaction.watch) ? reaction.watch : [reaction.watch];
    const prefixedFirstWatch = namePrefix ? `${namePrefix}.${watches[0]}` : watches[0];
    const firstWatchValue = getNestedValue(formValues, prefixedFirstWatch);

    const ctx: ConditionContext = {
      fieldValue: firstWatchValue,
      values: formValues,
      meta,
      getOptionProp,
    };

    const compute = reaction.compute;
    let result: unknown;

    if (isCompute(compute)) {
      const resolvedCompute = namePrefix
        ? prefixCompute(compute as Compute, namePrefix)
        : (compute as Compute);
      result = evaluateCompute(resolvedCompute, {
        fieldValue: firstWatchValue,
        values: formValues,
        meta,
        getOptionProp,
      });
    } else if (isCondition(compute)) {
      const prefixedCompute = namePrefix ? prefixCondition(compute as Condition, namePrefix) : compute;
      result = evaluateCondition(prefixedCompute as Condition, ctx);
    } else {
      result = compute;
    }

    switch (reaction.set) {
      case 'visible':
        overrides.visible = Boolean(result);
        break;
      case 'required':
        overrides.required = Boolean(result);
        break;
      case 'disabled':
        overrides.disabled = Boolean(result);
        break;
      case 'readOnly':
        overrides.readOnly = Boolean(result);
        break;
      case 'options':
        if (Array.isArray(result)) {
          overrides.options = result as Array<{ label: string; value: unknown }>;
        }
        break;
      case 'label':
        if (typeof result === 'string') overrides.label = result;
        break;
      case 'placeholder':
        if (typeof result === 'string') overrides.placeholder = result;
        break;
      case 'minDate':
        if (typeof result === 'string') overrides.minDate = result;
        break;
      case 'maxDate':
        if (typeof result === 'string') overrides.maxDate = result;
        break;
    }
  }

  return overrides;
}

export function useReactions({
  reactions,
  form,
  meta,
  getOptionProp,
  namePrefix,
}: UseReactionsParams): ReactionOverrides {
  const watchFields = useMemo(() => {
    if (!reactions || reactions.length === 0) return [] as string[];
    const fields = new Set<string>();
    for (const reaction of reactions) {
      const watches = Array.isArray(reaction.watch) ? reaction.watch : [reaction.watch];
      watches.forEach((f) => fields.add(namePrefix ? `${namePrefix}.${f}` : f));
    }
    return Array.from(fields);
  }, [reactions, namePrefix]);

  const watchedValues = useWatch({
    control: form.control,
    name: watchFields,
    disabled: watchFields.length === 0,
  });

  return useMemo(
    () =>
      computeReactionOverrides({
        reactions,
        formValues: form.getValues() as Record<string, unknown>,
        meta,
        getOptionProp,
        namePrefix,
      }),
    [reactions, watchedValues, form, meta, getOptionProp, namePrefix],
  );
}

function isCondition(obj: unknown): obj is Condition {
  if (obj == null || typeof obj !== 'object') return false;
  const keys = Object.keys(obj as object);
  return keys.some((k) => (CONDITION_KEYS as readonly string[]).includes(k));
}

function isCompute(obj: unknown): obj is Compute {
  return obj != null && typeof obj === 'object' && 'operator' in (obj as object);
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}
